import React from "react";
import { SkinProvider, useSkin } from "../../skins/SkinContext";
import { studioSkin, handDrawnSkin } from "../../skins/skins";
import { SkRect, SkEllipse, SkCircle, SkLine, SkPolygon } from "../../prim";

/*
 * Anivaze v3 — Phase-1 proof of the STYLE-SKIN engine. One landscape, built from
 * skinnable primitives, rendered TWICE: left = Studio (clean vector), right =
 * Hand-drawn (Rough.js, seeded). Same code, two "types of video" — no filters.
 * Next: drive the skin from the style picker so the WHOLE video switches.
 */

// The scene content — composed ONLY from skinnable primitives. Animation is via
// <g transform> (rotate/translate) so the hand-drawn seeds stay stable.
const DemoLandscape: React.FC<{ t: number }> = ({ t }) => {
  const skin = useSkin();
  const W = 360, H = 410, ground = 280;
  const sunRot = (t * 14) % 360;
  const cloudX = ((t * 16) % (W + 140)) - 70;
  const wave = Math.sin(t * 5) * 22;
  return (
    <g>
      {/* surface (per-skin) + grass */}
      <rect width={W} height={H} fill={skin.surface} />
      <SkRect x={0} y={ground} w={W} h={H - ground} fill="#86efac" seed={11} />

      {/* sun — rays rotate via transform (no re-gen) */}
      <g transform="translate(66 70)">
        <g transform={`rotate(${sunRot})`}>
          {Array.from({ length: 8 }, (_, i) => {
            const a = (i / 8) * Math.PI * 2;
            return <SkLine key={i} x1={Math.cos(a) * 30} y1={Math.sin(a) * 30} x2={Math.cos(a) * 42} y2={Math.sin(a) * 42} stroke="#f59e0b" seed={20 + i} />;
          })}
        </g>
        <SkCircle cx={0} cy={0} r={24} fill="#fbbf24" seed={3} />
      </g>

      {/* drifting cloud */}
      <g transform={`translate(${cloudX} 64)`}>
        <SkEllipse cx={0} cy={0} rx={26} ry={16} fill="#ffffff" seed={31} />
        <SkEllipse cx={22} cy={6} rx={20} ry={13} fill="#ffffff" seed={32} />
      </g>

      {/* tree */}
      <g transform="translate(86 0)">
        <SkRect x={-6} y={ground - 46} w={12} h={46} fill="#92400e" seed={41} />
        <SkCircle cx={0} cy={ground - 60} r={30} fill="#22c55e" seed={42} />
      </g>

      {/* house */}
      <g transform="translate(260 0)">
        <SkRect x={-50} y={ground - 72} w={100} h={72} fill="#fcd34d" seed={51} />
        <SkPolygon points={[[-58, ground - 72], [0, ground - 116], [58, ground - 72]]} fill="#ef4444" seed={52} />
        <SkRect x={-14} y={ground - 42} w={28} h={42} fill="#7c3aed" seed={53} />
        <SkRect x={18} y={ground - 62} w={22} h={22} fill="#bae6fd" seed={54} />
      </g>

      {/* a waving person */}
      <g transform="translate(178 0)">
        <SkLine x1={0} y1={ground - 40} x2={0} y2={ground - 12} seed={61} />
        <SkCircle cx={0} cy={ground - 50} r={10} fill="#fcd34d" seed={62} />
        <SkLine x1={0} y1={ground - 32} x2={-11} y2={ground - 20} seed={63} />
        <g transform={`rotate(${wave} 0 ${ground - 32})`}>
          <SkLine x1={0} y1={ground - 32} x2={12} y2={ground - 46} seed={64} />
        </g>
        <SkLine x1={0} y1={ground - 12} x2={-7} y2={ground} seed={65} />
        <SkLine x1={0} y1={ground - 12} x2={7} y2={ground} seed={66} />
      </g>
    </g>
  );
};

export interface SkinDemoSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  t?: number;
}

export const SkinDemoScene: React.FC<SkinDemoSceneProps> = ({
  opacity = 1, t = 0, label = "Same scene, two styles — switch the whole video, no filters.",
}) => (
  <g opacity={opacity}>
    <rect width={800} height={450} fill="#0f172a" />
    <g transform="translate(18 8)"><SkinProvider skin={studioSkin}><DemoLandscape t={t} /></SkinProvider></g>
    <line x1={400} y1={8} x2={400} y2={418} stroke="#475569" strokeWidth={2} strokeDasharray="3 7" />
    <g transform="translate(420 8)"><SkinProvider skin={handDrawnSkin}><DemoLandscape t={t} /></SkinProvider></g>
    <text x={198} y={30} textAnchor="middle" fill="#0f172a" fontSize={14} fontWeight={700} fontFamily="Inter,sans-serif">Studio (clean vector)</text>
    <text x={600} y={30} textAnchor="middle" fill="#1a1a1a" fontSize={15} fontWeight={700} fontFamily="'Caveat','Comic Sans MS',Inter,sans-serif">Hand-drawn (Rough.js)</text>
    <text x={400} y={440} textAnchor="middle" fill="#cbd5e1" fontSize={14} fontWeight={600} fontFamily="Inter,sans-serif">{label}</text>
  </g>
);
